import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import React, { useMemo, useState } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { safeDivide } from 'src/utils/app';
import useAtlasV2DorisColumnData from 'src/serverProxy/useAtlasV2DorisColumnData';
import { useDorisEntityDropdownOptions } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficDoubleDropdownTopEntity';
import { DorisDropdownFieldNameEnum } from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { useEntityMetadataEnhancer } from 'src/components/AtlasV2Components/Hooks/useEntityMetadataEnhancer';
import SearchableDropdown from 'src/components/AtlasV2Components/Common/SearchableDropdown';
import { titleCase } from 'src/utils/stringFormatting';

const fieldOptions = [
  {
    label: 'Market Share of Retail Sales',
    id: 'retailSales',
    fieldName: 'retailSales',
    marketMetric: 'retailSales'
  },
  {
    label: '1P Market Share of Retail Sales',
    id: 'FirstPartySales/retailSales',
    fieldName: 'FirstPartySales',
    marketMetric: 'retailSales'
  },
  {
    label: '1P Market Share of 1P Retail Sales',
    id: 'FirstPartySales/onePRetailSales',
    fieldName: 'FirstPartySales',
    marketMetric: 'FirstPartySales'
  },
  {
    label: '3P Market Share of Retail Sales',
    id: 'ThirdPartySales',
    fieldName: 'ThirdPartySales',
    marketMetric: 'retailSales'
  },
  {
    label: '3P Market Share of 3P Retail Sales',
    id: 'ThirdPartySales/ThirdPartySales',
    fieldName: 'ThirdPartySales',
    marketMetric: 'ThirdPartySales'
  }
];

interface AtlasSalesMarketShareColumnChartProps {
  enableSearchableGroupBy?: boolean;
}

/**
 *
 * Sales Summary double dropdown
 */
const AtlasSalesMarketShareColumnChart = ({ enableSearchableGroupBy }: AtlasSalesMarketShareColumnChartProps) => {
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const groupByOptions = useDorisEntityDropdownOptions(DorisDropdownFieldNameEnum.Sales);

  const { enhanceMetadata } = useEntityMetadataEnhancer();

  const [selectedMainMetricField, setSelectedMainMetricField] = useState('retailSales');

  const [selectedGroupByFieldId, setSelectedGroupByFieldId] = useState('brand');
  const [selectedCustomTag, setSelectedCustomTag] = useState<string | null>(null);

  const isCustomMode = enableSearchableGroupBy && selectedGroupByFieldId === 'productAttributeValue';

  const currentGroupByOption = useMemo(() => {
    if (isCustomMode) {
      return {
        id: 'productAttributeValue',
        label: titleCase(selectedCustomTag || 'Product Attribute'),
        indexName: 'sales'
      } as unknown as { id: string; label: string; indexName?: string };
    }
    return groupByOptions.find((option) => option.id === selectedGroupByFieldId) || groupByOptions[0];
  }, [groupByOptions, isCustomMode, selectedCustomTag, selectedGroupByFieldId]);

  const currentMetricOption = fieldOptions.find((option) => option.id === selectedMainMetricField);

  const { data: targetData, isLoading: isTargetLoading } = useAtlasV2DorisColumnData({
    metricType: currentMetricOption.fieldName,
    fieldName: selectedGroupByFieldId,
    queryKey: 'Atlas-retailSales-target',
    endPoint: 'sales/GetSalesMetrics',
    additionalParams: isCustomMode && selectedCustomTag ? { productTags: [selectedCustomTag] } : undefined
  });

  const { data: marketShareData, isLoading: isMarketShareLoading } = useAtlasV2DorisColumnData({
    metricType: currentMetricOption.marketMetric,
    fieldName: 'retailer',
    queryKey: 'Atlas-retailSales-market',
    endPoint: 'sales/GetSalesMetrics'
  });

  const parsedData = useMemo(() => {
    const comparisonData = [];
    const currentData = [];
    const categoryData = [];

    if (!isMarketShareLoading && !isTargetLoading) {
      const currentMarketShare = marketShareData?.[0]?.values?.retailer;
      const compareMarketShare = marketShareData?.[0]?.comparisonValues?.retailer;

      targetData.forEach((datum) => {
        // Enhance metadata with category/subcategory information using the hook
        const enhancedMetadata = enhanceMetadata(datum, currentGroupByOption?.id);

        const datumName = enhancedMetadata.name || datum?.metadata?.name || datum?.fieldId;
        const currentDatum = datum?.values?.[currentGroupByOption?.id] ?? 0;
        const comparisonDatum = datum?.comparisonValues?.[currentGroupByOption?.id] ?? 0;

        comparisonData.push([datumName, safeDivide(comparisonDatum, compareMarketShare)]);
        currentData.push([datumName, safeDivide(currentDatum, currentMarketShare)]);
        categoryData.push(enhancedMetadata);
      });
    }

    return { currentData, categoryData, comparisonData };
  }, [currentGroupByOption, isMarketShareLoading, isTargetLoading, marketShareData, targetData, enhanceMetadata]);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        const label = `Category Market Share by ${currentGroupByOption.label}`;

        return saveBarChartAsDelimitedData(parsedData.currentData, parsedData.comparisonData, {
          fileTitle: `Retail Sales - ${label}`,
          metricLabel: label
        });
      }}
      includeSummaryInfo
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={isMarketShareLoading || isTargetLoading}
      loadingComponent={<TopEntityChartV2Loading enableSubTitleSkeleton />}
      title={
        <Flex alignItems="center">
          <SlDropdownMenu
            buttonVariant="title"
            options={fieldOptions}
            selectedId={selectedMainMetricField}
            onChange={(option) => setSelectedMainMetricField(option.id)}
            defaultLabel="Market Share of Retail Sales"
          />
          <Text variant="h4" sx={{ margin: '0 8px' }}>
            by
          </Text>
          {enableSearchableGroupBy ? (
            <SearchableDropdown
              buttonVariant="title"
              standardOptions={groupByOptions}
              selectedId={selectedGroupByFieldId}
              selectedCustomTag={selectedCustomTag || undefined}
              onChange={(opt) => {
                setSelectedCustomTag(null);
                setSelectedGroupByFieldId(opt.id);
              }}
              onCustomSelect={(tag) => {
                setSelectedCustomTag(tag);
                setSelectedGroupByFieldId('productAttributeValue');
              }}
              defaultLabel="Brand"
            />
          ) : (
            <SlDropdownMenu
              buttonVariant="title"
              options={groupByOptions}
              selectedId={selectedGroupByFieldId}
              onChange={(option) => setSelectedGroupByFieldId(option.id)}
              defaultLabel="Brand"
            />
          )}
        </Flex>
      }
    >
      <TopEntityChartV2
        chartSeries={[
          {
            type: 'column',
            data: parsedData.comparisonData,
            categories: parsedData.categoryData,
            metricType: METRICTYPE.PERCENT
          },
          {
            type: 'column',
            data: parsedData.currentData,
            categories: parsedData.categoryData,
            metricType: METRICTYPE.PERCENT
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasSalesMarketShareColumnChart;
